import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import emailValidator from 'email-validator';
import { TextField } from 'seagull';
import { routes } from '~/constants';
import { useStoreActions } from '~/contexts';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useLocaleKeys } from '~/i18n';
import { Footer, FormTitle, FooterText, FormFields, FooterLink, ErrorMessage, FormContainer, FormDescription, SubmitButtonField, VerticalFormFields } from '../components/FormComponents';
import UnauthorizedPageContainer from '../components/UnauthorizedPageContainer';
const SentScreen = (param)=>{
    let { email, tryAgain } = param;
    const { t: texts } = useLocaleKeys('pages.auth.resetPassword');
    return /*#__PURE__*/ _jsx(UnauthorizedPageContainer, {
        testId: "reset-email-sent-msg",
        children: /*#__PURE__*/ _jsxs(FormContainer, {
            children: [
                /*#__PURE__*/ _jsx(FormTitle, {
                    children: texts.checkYourEmail()
                }),
                /*#__PURE__*/ _jsx(FormDescription, {
                    "data-testid": "reset-password-sent-description",
                    children: texts.sentDescription({
                        email
                    })
                }),
                /*#__PURE__*/ _jsxs(Footer, {
                    children: [
                        /*#__PURE__*/ _jsx(FooterText, {
                            children: texts.didntReceive()
                        }),
                        /*#__PURE__*/ _jsx(FooterLink, {
                            onClick: tryAgain,
                            children: texts.wrongEmail()
                        })
                    ]
                })
            ]
        })
    });
};
const FormScreen = (param)=>{
    let { onSuccess } = param;
    const { t: texts } = useLocaleKeys('pages.auth.resetPassword');
    const { api, logBI } = useStoreActions();
    const [email, setEmail] = useState('');
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [emailWasBlurred, setEmailWasBlurred] = useState(false);
    const [resetInProgress, setResetInProgress] = useState(false);
    const [resetPasswordError, setResetPasswordError] = useState(null);
    const showEmailFieldError = email !== '' && !isEmailValid && emailWasBlurred;
    return /*#__PURE__*/ _jsx(UnauthorizedPageContainer, {
        testId: "reset-password-form",
        children: /*#__PURE__*/ _jsxs(FormContainer, {
            onChange: ()=>setResetPasswordError(null),
            onSubmit: async (event)=>{
                logBI(10600, {});
                event.preventDefault();
                setResetInProgress(true);
                try {
                    await api.auth.resetPassword(email);
                    setResetInProgress(false);
                    onSuccess(email);
                } catch (e) {
                    setResetInProgress(false);
                    setResetPasswordError(texts.failedToReset());
                }
            },
            children: [
                /*#__PURE__*/ _jsx(FormTitle, {
                    children: texts.title()
                }),
                /*#__PURE__*/ _jsx(FormDescription, {
                    children: texts.formDescription()
                }),
                /*#__PURE__*/ _jsx(FormFields, {
                    children: /*#__PURE__*/ _jsx(VerticalFormFields, {
                        children: /*#__PURE__*/ _jsx(TextField, {
                            bi: {
                                name: 'reset_password_email'
                            },
                            collapseHelperText: false,
                            autoFocus: true,
                            label: texts.emailLabel(),
                            type: "email",
                            value: email,
                            onChange: (event)=>{
                                const value = event.target.value;
                                const isValid = emailValidator.validate(value);
                                setEmail(value);
                                setIsEmailValid(isValid);
                            },
                            error: showEmailFieldError,
                            onBlur: ()=>setEmailWasBlurred(true),
                            testId: "email-input"
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(SubmitButtonField, {
                    disabled: !isEmailValid,
                    isLoading: resetInProgress,
                    testId: "reset-password-button",
                    children: texts.sendResetLink()
                }),
                resetPasswordError && /*#__PURE__*/ _jsx(ErrorMessage, {
                    testId: "error-msg",
                    children: resetPasswordError
                }),
                /*#__PURE__*/ _jsx(Footer, {
                    children: /*#__PURE__*/ _jsx(FooterLink, {
                        toInternalLink: routes.login,
                        testId: "back-to-login-button",
                        children: texts.backToSignIn()
                    })
                })
            ]
        })
    });
};
export function ResetPasswordScreen() {
    const [email, setEmail] = useState(null);
    useSetDocumentTitle('Anchor Reset Password');
    return email ? /*#__PURE__*/ _jsx(SentScreen, {
        email: email,
        tryAgain: ()=>setEmail(null)
    }) : /*#__PURE__*/ _jsx(FormScreen, {
        onSuccess: setEmail
    });
}
